/**
 * App.jsx
 * LUNA Coding Tutor React 앱 메인 컴포넌트
 * 
 * 원본 main.js의 초기화 로직 및 라우팅 처리
 */

import { useEffect, useCallback, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import useLunaStore from './store/useLunaStore';
import useLunaFlow from './hooks/useLunaFlow';
import { useSessionRestore } from './hooks/useSessionRestore';
import Sidebar, { useRewardHandler } from './components/sidebar/Sidebar';
import ChatInterface from './components/chat/ChatInterface';
import LessonPanel from './components/lesson/LessonPanel';
import DashboardPage from './pages/DashboardPage';
import RoadmapPage from './pages/RoadmapPage';
import LoginPage from './pages/LoginPage';
import AdminPage from './pages/AdminPage';
import LeaderboardPage from './pages/LeaderboardPage';
import AdminAnalyticsPage from './pages/AdminAnalyticsPage';
import IntroPage from './pages/IntroPage';
import Header from './components/layout/Header';
import WelcomeScreen from './components/welcome/WelcomeScreen';
import PasswordChangeModal from './components/modals/PasswordChangeModal';
import TermsModal from './components/modals/TermsModal';
import LevelUpModal from './components/modals/LevelUpModal';
import api from './services/api';
import { DEMO_CURRICULUM, DEMO_CONVERSATIONS, DEMO_LESSON } from './data/demoData';

// ============================================
// 루트 라우트 핸들러 (무조건 /app으로 리다이렉트)
// ============================================
function RootRoute() {
  // [Mod] IntroPage 제거 - 외부 intro.html이 별도로 존재하므로
  // 모든 경우 /app으로 리다이렉트하여 게스트 데모 시퀀스 실행
  return <Navigate to="/app" replace />;
}

// ============================================
// 메인 학습 페이지 컴포넌트
// ============================================
function LunaMainPage() {
  const navigate = useNavigate();
  const {
    appState,
    currentCurriculum,
    currentLessonPlan,
    isRoadmapMode,
    chatHistory,
    setAppState,
    setCurrentCurriculum,
    setCurrentStepIndex,
    setCurrentLessonStepIndex,
    setLessonCompleted,
    setCurrentLessonPlan,
    addChatMessage,
    roadmapContext,
    username, // userName 확인용
  } = useLunaStore();

  const {
    handleChatSubmit,
    isLoading,
    loadingMessage,
    generateCurriculum,
    handleStepClick,
    resetApp
  } = useLunaFlow();

  const levelUpData = useLunaStore(s => s.levelUpData);
  const setLevelUpData = useLunaStore(s => s.setLevelUpData);

  // Modal states
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  // [Refactor] 세션 복원 및 초기화 로직 분리
  const isInitialized = useSessionRestore();

  // [Fix] 로드맵 토픽 전환 시 커리큘럼 자동 생성 (needsCurriculumGeneration 플래그 기반)
  // 이전: isRoadmapMode + roadmapContext 변화만으로 트리거 → 세션 복원과 충돌
  // 수정: needsCurriculumGeneration 플래그가 true일 때만 트리거 (handleStartLesson에서 명시적으로 설정)
  const needsCurriculumGeneration = useLunaStore(s => s.needsCurriculumGeneration);
  useEffect(() => {
    if (!isInitialized) return;
    if (!needsCurriculumGeneration) return;

    const state = useLunaStore.getState();
    const targetTopic = state.roadmapContext?.topic || state.intendedTopic;

    if (targetTopic) {
      console.log('[App] needsCurriculumGeneration 감지 → 커리큘럼 자동 생성:', targetTopic);
      generateCurriculum(targetTopic);
    } else {
      console.warn('[App] needsCurriculumGeneration=true인데 targetTopic이 없음, 플래그 클리어');
      state.setNeedsCurriculumGeneration(false);
    }
  }, [needsCurriculumGeneration, generateCurriculum, isInitialized]);

  // 사이드바 단계 클릭 핸들러
  const handleSidebarStepClick = useCallback(async (stepIndex, step) => {
    const isCurrentStep = stepIndex === useLunaStore.getState().currentStepIndex;

    const actionName = isCurrentStep ? "다시 학습" : "이동";
    const alertMessage = isCurrentStep
      ? `"${step.title}" 단원을 처음부터 다시 학습하시겠습니까?\n현재 진행 중인 내용이 초기화됩니다.`
      : `"${step.title}" 단원으로 이동하시겠습니까?\n해당 단원의 레슨이 새로 생성됩니다.`;

    const confirmed = window.confirm(alertMessage);
    if (!confirmed) return;

    handleStepClick(stepIndex);

  }, [useLunaStore, handleStepClick]);

  // 새 주제 시작
  const handleNewTopic = useCallback(() => {
    // [Fix] 로드맵 유지하면서 IDLE 상태로 전환 (상담을 통해 결정하도록 유도)
    const confirmed = window.confirm('새로운 학습 주제를 찾아볼까요?\n(현재 대화 내용은 초기화됩니다)');
    if (!confirmed) return;

    // [Fix] keepRoadmap: true -> 로드맵 데이터를 유지한 채로 리셋
    resetApp({ keepRoadmap: true });

    setAppState('IDLE');
    navigate('/app');
  }, [setAppState, navigate, resetApp]);

  // 로드맵 페이지로 이동
  const handleGoToRoadmap = useCallback(() => {
    navigate('/roadmap');
  }, [navigate]);

  // 대시보드 페이지로 이동
  const handleGoToDashboard = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  // [New] 게스트 자동 데모 시퀀스 (채팅 + 커리큘럼)
  const runGuestDemoSequence = useCallback(async () => {
    // 중복 실행 방지
    if (useLunaStore.getState().chatHistory.length > 0) return;

    console.log('[Demo] Auto-running guest demo sequence');

    // 1. 채팅 시뮬레이션
    for (const conv of DEMO_CONVERSATIONS) {
      await new Promise(r => setTimeout(r, conv.delay));
      addChatMessage({
        role: conv.role,
        content: conv.content
      });
    }

    // 2. 커리큘럼 설정
    setCurrentCurriculum(DEMO_CURRICULUM);
    setCurrentStepIndex(0);
  }, [addChatMessage, setCurrentCurriculum, setCurrentStepIndex]);

  // [New] 게스트 레슨 시작 핸들러 (버튼 클릭 시)
  const handleStartLesson = useCallback(() => {
    const store = useLunaStore.getState();
    console.log('[Demo] Starting demo lesson content');

    store.setCurrentLessonPlan(DEMO_LESSON);
    store.setCurrentLessonStepIndex(0);
    store.setLessonCompleted(false);
    setAppState('CONCEPT_VIEW'); // 레슨 화면으로 전환

    addChatMessage({
      role: 'ai',
      content: `좋아요! **"${DEMO_LESSON.title}"** 체험 레슨을 시작합니다. 🎯\n\n실제 수업과 똑같은 환경이에요. 화면의 설명을 따라가보세요!`
    });
  }, [setAppState, addChatMessage]);

  // 게스트 초기화 시 자동 실행
  useEffect(() => {
    if (!isInitialized) return;

    // [Fix] Store에서 직접 상태 확인 (React 상태 동기화 지연 문제 방지)
    const storeState = useLunaStore.getState();
    const isLoggedIn = !!storeState.username;
    const hasExistingCurriculum = !!storeState.currentCurriculum;

    // 로그인 사용자거나 이미 커리큘럼이 있으면 데모 스킵
    if (isLoggedIn || hasExistingCurriculum) {
      console.log('[APP] 로그인 사용자 또는 기존 커리큘럼 존재 - 게스트 데모 스킵');
      return;
    }

    runGuestDemoSequence();
  }, [isInitialized, runGuestDemoSequence]);

  // 레이아웃 결정: 수업 진행 중이면 분할 뷰
  const showLessonPanel = currentLessonPlan &&
    ['CONCEPT_VIEW', 'LESSON_EXPLAINED', 'PROBLEM_SOLVING'].includes(appState);

  /*
    [Fix] 로그아웃 핸들러 개선
    - 로그아웃 후 메인 페이지로 이동하여 게스트 체험 모드(채팅 시뮬레이션 및 샘플 레슨) 실행
    - 사용자가 자연스럽게 서비스를 경험하도록 유도
  */
  const handleLogout = useCallback(async () => {
    try {
      await api.logout();
    } catch (e) {
      console.warn('Logout failed', e);
    }

    // Store 상태 클리어
    useLunaStore.getState().clearUserSession();

    // 사용자 식별 데이터 클리어
    localStorage.removeItem('coding_tutor_user_id');
    localStorage.removeItem('luna-storage');

    // 메인 페이지로 이동 (게스트 데모 시퀀스 자동 실행)
    window.location.href = '/';
  }, []);

  // [Fix] 초기화 전에는 로딩 표시 (깜빡임 방지)
  if (!isInitialized) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-[#0B0E14] text-cyan-500">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-cyan-500/30 border-t-cyan-500 rounded-full animate-spin"></div>
          <p className="text-sm tracking-widest animate-pulse">LUNA SYSTEM INITIALIZING...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="luna-app h-screen flex flex-col bg-[#0B0E14] text-white font-sans overflow-hidden relative">
      {/* Global Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="aurora-blob blob-1"></div>
        <div className="aurora-blob blob-2"></div>
        <div className="aurora-blob blob-3"></div>
        <div className="bg-grid-pattern"></div>
      </div>
      <Header
        onNavigate={navigate}
        onNewTopic={handleNewTopic}
        onLogout={handleLogout}
        onPasswordChange={() => setIsPasswordModalOpen(true)}
        onShowTerms={() => setIsTermsModalOpen(true)}
      />

      {/* 모달들 */}
      <PasswordChangeModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />

      {/* 2. 메인 그리드 레이아웃 (12분할) */}
      <main className="flex-grow grid grid-cols-12 gap-3 p-3 min-h-0 overflow-hidden">
        {/* 레벨업 모달 (전역) */}
        <LevelUpModal
          show={!!levelUpData}
          level={levelUpData?.level}
          onClose={() => setLevelUpData(null)}
        />

        {/* 3. 좌측 사이드바 (2칸 = 약 16%) */}
        <div className="col-span-2 flex flex-col min-h-0 overflow-hidden rounded-2xl">
          <Sidebar
            className="w-full h-full border-none"
            onStepClick={handleSidebarStepClick}
            onNewTopic={handleNewTopic}
            onRoadmap={handleGoToRoadmap}
            onDashboard={handleGoToDashboard}
            hideHeader={true}
          />
        </div>

        {/* 4. 중앙 채팅창 (3칸 = 약 25%) */}
        <section className="col-span-3 flex flex-col rounded-2xl overflow-hidden min-h-0 relative">
          <ChatInterface
            onSubmit={handleChatSubmit}
            isLoading={isLoading}
            loadingMessage={loadingMessage}
          />
        </section>

        {/* 5. 우측 에디터/컨텐츠 (7칸 = 약 58%) */}
        <section className="col-span-7 flex flex-col rounded-2xl overflow-hidden min-h-0">
          {/* 수업 패널 (조건부 렌더링) */}
          {showLessonPanel ? (
            <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
              <LessonPanel />
            </div>
          ) : (
            /* 환영 화면 (수업 패널이 없을 때) */
            <WelcomeScreen
              onTopicSelect={(topic) => {
                // 태그 클릭 시 채팅창에 자동 입력 (전송 X)
                useLunaStore.getState().setChatInputValue(topic);
              }}
              onCardClick={(type, text) => {
                // 카드 클릭 시 채팅창에 예시 텍스트 입력 (전송 X)
                useLunaStore.getState().setChatInputValue(text);
              }}
              onStartDemo={handleStartLesson}
              isDemoReady={!!currentCurriculum && !username}
            />
          )}
        </section>
      </main>
    </div>
  );
}

// ============================================
// 앱 라우터
// ============================================
export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<RootRoute />} />
        <Route path="/app" element={<LunaMainPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/roadmap" element={<RoadmapPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<LoginPage initialTab="register" />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
        <Route path="/app/luna/intro" element={<IntroPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
